// combatpro/runtime/GeneratedSlotCard.js
//
// Combat PRO - one Random / Wave generated encounter slot card (spec 11, 18).
// State-driven safe actions (spec 10, 17): EMPTY / GENERATED-READY / ACTIVE / USED.
// Reroll and Clear never silently destroy an active fight (the active card offers
// only Continue + Rename; Clear on the active slot is confirmed in the handler).
// Reuses the existing Combat PRO card visual language. No optional chaining. No em dash.

import React, { useState } from 'react';
import { DIFFICULTY_LABELS, CREATURE_TYPE_LABELS, COMPOSITION_LABELS, WAVE_STYLE_LABELS } from '../encounter/encounterGenEngine';

var GEN_STATE_META = {
  empty:     { key: 'empty',   label: 'Empty',     emoji: '\u26aa' },
  generated: { key: 'ready',   label: 'Generated', emoji: '\ud83d\udfe2' },
  ready:     { key: 'ready',   label: 'Ready',     emoji: '\ud83d\udfe2' },
  active:    { key: 'active',  label: 'Active',    emoji: '\ud83d\udd34' },
  used:      { key: 'used',    label: 'Used',      emoji: '\ud83d\udfe1' }
};

function populatedCount(combat) {
  if (!combat || !Array.isArray(combat.monsters)) { return 0; }
  return combat.monsters.filter(function (m) {
    return m && ((m.customName && String(m.customName).trim()) || (m.selectedMonster && String(m.selectedMonster).trim()));
  }).length;
}

function GeneratedSlotCard(props) {
  var family = props.family;
  var isWave = (family === 'wave');
  var i = props.index;
  var slot = props.slot || {};
  var meta = slot.meta || {};
  var gen = slot.gen || {};
  var stateKey = (gen.state && GEN_STATE_META[gen.state]) ? gen.state : 'empty';
  var st = GEN_STATE_META[stateKey];
  var isActive = props.isActive;
  var ctx = props.ctx;
  var fallbackName = (isWave ? 'Wave Encounter ' : 'Random Encounter ') + (i + 1);

  var editHook = useState(false);
  var editing = editHook[0];
  var setEditing = editHook[1];
  var nameHook = useState(meta.name || fallbackName);
  var nameVal = nameHook[0];
  var setNameVal = nameHook[1];

  function beginRename() { setNameVal(meta.name || fallbackName); setEditing(true); }
  function commitRename() { ctx.renameEncounter(family, i, nameVal); setEditing(false); }

  var count = populatedCount(slot.combat);
  var isEmpty = (stateKey === 'empty');
  var saved = !!(meta && meta.saved);
  var cardClass = 'cp-slot-card cp-slot-' + st.key + (isActive ? ' is-active' : '');

  return (
    <div className={cardClass}>
      <div className="cp-slot-accent"></div>
      <div className="cp-slot-head">
        {editing ? (
          <input
            className="cp-slot-name-input"
            value={nameVal}
            autoFocus
            onChange={function (e) { setNameVal(e.target.value); }}
            onKeyDown={function (e) { if (e.key === 'Enter') { commitRename(); } if (e.key === 'Escape') { setEditing(false); } }}
          />
        ) : (
          <div className="cp-slot-name" title={meta.name || fallbackName}>{meta.name || fallbackName}</div>
        )}
        <span className={'cp-slot-badge cp-badge-' + st.key}>{st.emoji + ' ' + st.label}</span>
        {saved ? <span className="cp-slot-badge cp-badge-saved">{'\ud83d\udcbe Saved'}</span> : null}
      </div>
      <div className="cp-slot-sub">{(isWave ? 'Wave ' : 'Random ') + (i + 1) + (isActive ? ' \u00b7 Active now' : '')}</div>

      {isEmpty ? (
        <div className="cp-slot-empty">Empty slot. Set the presets above, then Generate.</div>
      ) : (
        <React.Fragment>
          <div className="cp-slot-headline">{gen.headline || gen.summary || ''}</div>
          <div className="cp-slot-stats">
            <span>{'\ud83c\udfaf ' + (DIFFICULTY_LABELS[gen.difficulty] || gen.difficulty || '\u2014')}</span>
            <span>{'\u2b50 Level ' + (gen.level || '\u2014')}</span>
            {isWave ? (
              <span>{'\ud83c\udf0a ' + (gen.waveCount || 0) + ' waves'}</span>
            ) : (
              <span>{'\ud83e\udde9 ' + (COMPOSITION_LABELS[gen.composition] || gen.composition || '\u2014')}</span>
            )}
            <span>{'\ud83d\udc79 ' + count + ' monsters'}</span>
            <span>{'\ud83e\uddec ' + (CREATURE_TYPE_LABELS[gen.creatureType] || gen.creatureType || 'Any')}</span>
            {isWave ? (
              <span>{'\ud83c\udfad ' + (WAVE_STYLE_LABELS[gen.style] || gen.style || '\u2014')}</span>
            ) : null}
          </div>
          {isWave && Array.isArray(gen.waveSummary) && gen.waveSummary.length ? (
            <div className="cp-slot-waves">
              {gen.waveSummary.map(function (w, wi) { return <div className="cp-slot-wave-line" key={wi}>{w}</div>; })}
            </div>
          ) : (
            slot.combat && !isWave ? <div className="cp-slot-monsters">{gen.summary || ''}</div> : null
          )}
        </React.Fragment>
      )}

      <div className="cp-slot-actions">
        {isEmpty ? (
          <React.Fragment>
            <button className="cp-slot-btn cp-slot-primary" onClick={function () { props.onGenerate(i); }}>Generate</button>
            <button className="cp-slot-btn" onClick={beginRename}>Rename</button>
          </React.Fragment>
        ) : isActive ? (
          <React.Fragment>
            <button className="cp-slot-btn cp-slot-primary" onClick={function () { ctx.runEncounter(family, i); }}>Continue</button>
            <button className="cp-slot-btn" onClick={beginRename}>Rename</button>
          </React.Fragment>
        ) : stateKey === 'used' ? (
          <React.Fragment>
            <button className="cp-slot-btn cp-slot-primary" onClick={function () { ctx.runEncounter(family, i); }}>View</button>
            <button className="cp-slot-btn" onClick={function () { props.onReroll(i); }}>Reroll</button>
            <button className="cp-slot-btn" onClick={beginRename}>Rename</button>
            <button className="cp-slot-btn" onClick={function () { ctx.promoteGeneratedToStandard(family, i); }}>Promote</button>
            <button className="cp-slot-btn cp-slot-danger" onClick={function () { ctx.clearGeneratedSlot(family, i); }}>Clear</button>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <button className="cp-slot-btn cp-slot-primary" onClick={function () { ctx.runEncounter(family, i); }}>Run</button>
            <button className="cp-slot-btn" onClick={function () { props.onReroll(i); }}>Reroll</button>
            <button className="cp-slot-btn" onClick={beginRename}>Rename</button>
            <button className="cp-slot-btn" onClick={function () { ctx.promoteGeneratedToStandard(family, i); }}>Promote</button>
            <button className="cp-slot-btn cp-slot-danger" onClick={function () { ctx.clearGeneratedSlot(family, i); }}>Clear</button>
          </React.Fragment>
        )}
      </div>

      {!isEmpty ? (
        <div className="cp-slot-save-row">
          <button className={'cp-slot-btn cp-slot-save' + (saved ? ' is-saved' : '')} onClick={function () { ctx.setEncounterSaved(family, i, !saved); }}>
            {saved ? 'Saved \u2713 (tap to unsave)' : 'Save to cloud'}
          </button>
        </div>
      ) : null}

      {editing ? (
        <div className="cp-slot-rename-row">
          <button className="cp-slot-btn cp-slot-primary" onClick={commitRename}>Save name</button>
          <button className="cp-slot-btn" onClick={function () { setEditing(false); }}>Cancel</button>
        </div>
      ) : null}
    </div>
  );
}

export default GeneratedSlotCard;
